import { useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Columns2,
  FilePlus2,
  GripVertical,
  ImageIcon,
  LayoutTemplate,
  Link,
  Mail,
  Monitor,
  MousePointerClick,
  Network,
  Palette,
  PanelTop,
  Save,
  Smartphone,
  Space,
  Text,
  Trash2,
  Type,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

export const DEFAULT_EMAIL_DESIGN = {
  width: 600,
  body_background: "#f5f7f8",
  content_background: "#ffffff",
  font_family: "Arial, sans-serif",
  text_color: "#334155",
};

const BLOCK_LIBRARY = [
  { type: "heading", label: "Titolo", icon: Type, description: "Titolo o messaggio in evidenza" },
  { type: "text", label: "Testo", icon: Text, description: "Paragrafi e contenuti dell'email" },
  { type: "image", label: "Immagine", icon: ImageIcon, description: "Banner, logo o fotografia" },
  { type: "cta", label: "Pulsante", icon: MousePointerClick, description: "Call to action con link" },
  { type: "columns", label: "Due colonne", icon: Columns2, description: "Contenuti affiancati" },
  { type: "social", label: "Social media", icon: Network, description: "Link a sito e social" },
  { type: "divider", label: "Separatore", icon: PanelTop, description: "Linea di divisione" },
  { type: "spacer", label: "Spaziatore", icon: Space, description: "Spazio vuoto tra i blocchi" },
];

const FONT_OPTIONS = [
  ["Arial, sans-serif", "Arial"],
  ["Trebuchet MS, Arial, sans-serif", "Trebuchet MS"],
  ["Georgia, serif", "Georgia"],
  ["Verdana, sans-serif", "Verdana"],
];

function blockId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `email-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function safeColor(value, fallback) {
  return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(value || "") ? value : fallback;
}

function numberInRange(value, fallback, min, max) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : fallback;
}

function defaultBlock(type) {
  const id = blockId();
  switch (type) {
    case "heading":
      return { id, type, content: "Il tuo titolo qui", align: "left", color: "#1e293b", font_size: 28, padding: 24 };
    case "text":
      return { id, type, content: "Inserisci qui il testo della tua comunicazione. Puoi personalizzarlo usando {{nome}} e {{organizzazione}}.", align: "left", color: "#475569", font_size: 16, padding: 24 };
    case "image":
      return { id, type, src: "", alt: "Immagine email", href: "", width: 100, align: "center", padding: 0 };
    case "cta":
      return { id, type, content: "Scopri di più", label: "Scopri di più", url: "", align: "center", background_color: "#0f766e", color: "#ffffff", border_radius: 8, padding: 24 };
    case "columns":
      return {
        id,
        type,
        left_title: "Primo contenuto",
        left_text: "Descrivi qui il primo punto importante.",
        right_title: "Secondo contenuto",
        right_text: "Descrivi qui il secondo punto importante.",
        color: "#334155",
        padding: 24,
      };
    case "social":
      return { id, type, website_url: "", linkedin_url: "", facebook_url: "", instagram_url: "", color: "#0f766e", align: "center", padding: 20 };
    case "divider":
      return { id, type, color: "#dbe3eb", thickness: 1, padding: 24 };
    case "spacer":
      return { id, type, height: 28 };
    default:
      return { id, type: "text", content: "" };
  }
}

export function normalizeEmailBlocks(value) {
  const blocks = Array.isArray(value) ? value : [];
  return blocks.map((block) => ({ ...defaultBlock(block?.type), ...block, id: block?.id || blockId() }));
}

function PaletteBlock({ definition, onAdd }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${definition.type}`,
    data: { source: "palette", blockType: definition.type },
  });
  const Icon = definition.icon;

  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`group flex min-h-24 flex-col items-center justify-center rounded-xl border bg-white px-2 text-center transition hover:border-teal-300 hover:bg-teal-50/50 ${isDragging ? "opacity-40" : ""}`}
      onClick={() => onAdd(definition.type)}
      {...attributes}
      {...listeners}
    >
      <Icon className="mb-2 h-5 w-5 text-teal-600" />
      <span className="text-xs font-semibold text-slate-700">{definition.label}</span>
      <span className="mt-0.5 hidden text-[10px] leading-tight text-slate-400 xl:block">{definition.description}</span>
    </button>
  );
}

function AlignPicker({ value = "left", onChange }) {
  const entries = [
    ["left", AlignLeft, "A sinistra"],
    ["center", AlignCenter, "Al centro"],
    ["right", AlignRight, "A destra"],
  ];
  return (
    <div className="inline-flex overflow-hidden rounded-md border bg-white">
      {entries.map(([alignment, Icon, label]) => (
        <button
          key={alignment}
          type="button"
          title={label}
          aria-label={label}
          className={`p-1.5 ${value === alignment ? "bg-teal-100 text-teal-800" : "text-slate-500 hover:bg-slate-50"}`}
          onClick={() => onChange(alignment)}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

function EditorField({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  const color = safeColor(value, "#334155");
  return (
    <EditorField label={label}>
      <div className="flex items-center gap-2">
        <input
          aria-label={label}
          className="h-8 w-10 cursor-pointer rounded border bg-white p-0.5"
          type="color"
          value={color}
          onChange={(event) => onChange(event.target.value)}
        />
        <Input className="h-8 text-xs" value={color} onChange={(event) => onChange(event.target.value)} />
      </div>
    </EditorField>
  );
}

function RangeField({ label, value, onChange, min, max, unit = "px" }) {
  const numeric = numberInRange(value, min, min, max);
  return (
    <EditorField label={`${label} · ${numeric}${unit}`}>
      <input
        className="h-2 w-full cursor-pointer accent-teal-600"
        type="range"
        min={min}
        max={max}
        value={numeric}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </EditorField>
  );
}

function BlockSettings({ block, onUpdate, onDelete }) {
  if (!block) {
    return (
      <div className="rounded-lg border border-dashed bg-white p-3 text-xs leading-relaxed text-slate-500">
        Seleziona un blocco nell&apos;email per modificarne contenuto, spaziatura e stile.
      </div>
    );
  }
  const update = (field, value) => onUpdate(block.id, field, value);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b pb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {BLOCK_LIBRARY.find((item) => item.type === block.type)?.label || block.type}
        </p>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => onDelete(block.id)} aria-label="Elimina blocco">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {(block.type === "heading" || block.type === "text") && (
        <>
          <EditorField label={block.type === "heading" ? "Titolo" : "Testo"}>
            <Textarea rows={block.type === "heading" ? 2 : 6} className="resize-y text-sm" value={block.content || ""} onChange={(event) => update("content", event.target.value)} />
          </EditorField>
          <div className="grid grid-cols-2 gap-2">
            <ColorField label="Colore" value={block.color} onChange={(value) => update("color", value)} />
            <EditorField label="Allineamento"><AlignPicker value={block.align} onChange={(value) => update("align", value)} /></EditorField>
          </div>
          <RangeField label="Dimensione carattere" value={block.font_size} onChange={(value) => update("font_size", value)} min={block.type === "heading" ? 18 : 12} max={block.type === "heading" ? 48 : 24} />
          <RangeField label="Spaziatura interna" value={block.padding} onChange={(value) => update("padding", value)} min={0} max={64} />
        </>
      )}

      {block.type === "image" && (
        <>
          <EditorField label="URL immagine">
            <Input type="url" placeholder="https://…/immagine.jpg" value={block.src || ""} onChange={(event) => update("src", event.target.value)} />
          </EditorField>
          <EditorField label="Testo alternativo">
            <Input value={block.alt || ""} onChange={(event) => update("alt", event.target.value)} />
          </EditorField>
          <EditorField label="Link al clic (facoltativo)">
            <Input type="url" placeholder="https://…" value={block.href || ""} onChange={(event) => update("href", event.target.value)} />
          </EditorField>
          <EditorField label="Allineamento"><AlignPicker value={block.align} onChange={(value) => update("align", value)} /></EditorField>
          <RangeField label="Larghezza immagine" value={block.width} onChange={(value) => update("width", value)} min={20} max={100} unit="%" />
          <RangeField label="Spaziatura verticale" value={block.padding} onChange={(value) => update("padding", value)} min={0} max={64} />
        </>
      )}

      {block.type === "cta" && (
        <>
          <EditorField label="Testo pulsante"><Input value={block.label || block.content || ""} onChange={(event) => { update("label", event.target.value); update("content", event.target.value); }} /></EditorField>
          <EditorField label="URL di destinazione"><Input type="url" placeholder="https://…" value={block.url || ""} onChange={(event) => update("url", event.target.value)} /></EditorField>
          <div className="grid grid-cols-2 gap-2">
            <ColorField label="Sfondo" value={block.background_color} onChange={(value) => update("background_color", value)} />
            <ColorField label="Testo" value={block.color} onChange={(value) => update("color", value)} />
          </div>
          <EditorField label="Allineamento"><AlignPicker value={block.align} onChange={(value) => update("align", value)} /></EditorField>
          <RangeField label="Angoli arrotondati" value={block.border_radius} onChange={(value) => update("border_radius", value)} min={0} max={32} />
          <RangeField label="Spaziatura verticale" value={block.padding} onChange={(value) => update("padding", value)} min={0} max={64} />
        </>
      )}

      {block.type === "columns" && (
        <>
          <EditorField label="Titolo sinistra"><Input value={block.left_title || ""} onChange={(event) => update("left_title", event.target.value)} /></EditorField>
          <EditorField label="Testo sinistra"><Textarea rows={3} value={block.left_text || ""} onChange={(event) => update("left_text", event.target.value)} /></EditorField>
          <EditorField label="Titolo destra"><Input value={block.right_title || ""} onChange={(event) => update("right_title", event.target.value)} /></EditorField>
          <EditorField label="Testo destra"><Textarea rows={3} value={block.right_text || ""} onChange={(event) => update("right_text", event.target.value)} /></EditorField>
          <ColorField label="Colore testo" value={block.color} onChange={(value) => update("color", value)} />
          <RangeField label="Spaziatura interna" value={block.padding} onChange={(value) => update("padding", value)} min={0} max={64} />
        </>
      )}

      {block.type === "social" && (
        <>
          <EditorField label="Sito web"><Input type="url" placeholder="https://…" value={block.website_url || ""} onChange={(event) => update("website_url", event.target.value)} /></EditorField>
          <EditorField label="LinkedIn"><Input type="url" placeholder="https://…" value={block.linkedin_url || ""} onChange={(event) => update("linkedin_url", event.target.value)} /></EditorField>
          <EditorField label="Facebook"><Input type="url" placeholder="https://…" value={block.facebook_url || ""} onChange={(event) => update("facebook_url", event.target.value)} /></EditorField>
          <EditorField label="Instagram"><Input type="url" placeholder="https://…" value={block.instagram_url || ""} onChange={(event) => update("instagram_url", event.target.value)} /></EditorField>
          <ColorField label="Colore link" value={block.color} onChange={(value) => update("color", value)} />
          <EditorField label="Allineamento"><AlignPicker value={block.align} onChange={(value) => update("align", value)} /></EditorField>
        </>
      )}

      {block.type === "divider" && (
        <>
          <ColorField label="Colore linea" value={block.color} onChange={(value) => update("color", value)} />
          <RangeField label="Spessore" value={block.thickness} onChange={(value) => update("thickness", value)} min={1} max={8} />
          <RangeField label="Spaziatura verticale" value={block.padding} onChange={(value) => update("padding", value)} min={0} max={64} />
        </>
      )}

      {block.type === "spacer" && <RangeField label="Altezza spazio" value={block.height} onChange={(value) => update("height", value)} min={8} max={160} />}
    </div>
  );
}

function EmailBlockPreview({ block, selected, onSelect }) {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.45 : 1 };
  const selectedClass = selected ? "ring-2 ring-teal-500 ring-offset-2" : "hover:ring-2 hover:ring-teal-200";
  const padding = numberInRange(block.padding, 0, 0, 64);
  const content = String(block.content || "").replaceAll("{{nome}}", "Mario Rossi").replaceAll("{{organizzazione}}", "RSA Il Glicine");
  const previewStyle = { fontFamily: "inherit", padding: `${padding}px`, textAlign: block.align || "left" };

  const renderBlock = () => {
    if (block.type === "heading") {
      return <h2 className="m-0 break-words font-bold leading-tight" style={{ ...previewStyle, color: safeColor(block.color, "#1e293b"), fontSize: `${numberInRange(block.font_size, 28, 18, 48)}px` }}>{content}</h2>;
    }
    if (block.type === "text") {
      return <p className="m-0 whitespace-pre-wrap break-words leading-relaxed" style={{ ...previewStyle, color: safeColor(block.color, "#475569"), fontSize: `${numberInRange(block.font_size, 16, 12, 24)}px` }}>{content}</p>;
    }
    if (block.type === "image") {
      const image = block.src
        ? <img src={block.src} alt={block.alt || ""} className="max-w-full" style={{ width: `${numberInRange(block.width, 100, 20, 100)}%`, display: "inline-block" }} />
        : <div className="inline-flex min-h-32 w-full items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-400"><ImageIcon className="mr-2 h-5 w-5" />Inserisci URL immagine</div>;
      return <div style={previewStyle}>{image}</div>;
    }
    if (block.type === "cta") {
      return <div style={previewStyle}><span className="inline-block px-5 py-3 text-sm font-bold" style={{ backgroundColor: safeColor(block.background_color, "#0f766e"), borderRadius: `${numberInRange(block.border_radius, 8, 0, 32)}px`, color: safeColor(block.color, "#ffffff") }}>{block.label || content || "Scopri di più"}</span></div>;
    }
    if (block.type === "columns") {
      return (
        <div className="grid gap-4 sm:grid-cols-2" style={{ padding: `${padding}px`, color: safeColor(block.color, "#334155") }}>
          <div className="rounded-lg bg-slate-50 p-4"><h3 className="m-0 text-base font-bold">{block.left_title}</h3><p className="mb-0 mt-2 whitespace-pre-wrap text-sm leading-relaxed">{block.left_text}</p></div>
          <div className="rounded-lg bg-slate-50 p-4"><h3 className="m-0 text-base font-bold">{block.right_title}</h3><p className="mb-0 mt-2 whitespace-pre-wrap text-sm leading-relaxed">{block.right_text}</p></div>
        </div>
      );
    }
    if (block.type === "social") {
      const links = [["Sito", block.website_url], ["LinkedIn", block.linkedin_url], ["Facebook", block.facebook_url], ["Instagram", block.instagram_url]].filter(([, url]) => url);
      return <div className="text-sm font-semibold" style={{ ...previewStyle, color: safeColor(block.color, "#0f766e") }}>{links.length ? links.map(([label], index) => <span key={label}>{index > 0 && <span className="px-2 text-slate-300">|</span>}{label}</span>) : "Aggiungi i tuoi link social"}</div>;
    }
    if (block.type === "divider") {
      return <div style={{ padding: `${padding}px` }}><div style={{ height: `${numberInRange(block.thickness, 1, 1, 8)}px`, backgroundColor: safeColor(block.color, "#dbe3eb") }} /></div>;
    }
    if (block.type === "spacer") return <div style={{ height: `${numberInRange(block.height, 28, 8, 160)}px` }} />;
    return null;
  };

  return (
    <div ref={setNodeRef} style={style} className={`group relative cursor-pointer transition ${selectedClass}`} onClick={onSelect}>
      <button
        type="button"
        aria-label="Trascina per ordinare il blocco"
        className="absolute left-1 top-1 z-10 cursor-grab rounded bg-slate-700/85 p-1 text-white shadow active:cursor-grabbing md:opacity-0 md:transition-opacity md:group-hover:opacity-100"
        onClick={(event) => event.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      {renderBlock()}
    </div>
  );
}

function EmailCanvas({ blocks, design, activeBlockId, onSelect, mobile }) {
  const { isOver, setNodeRef } = useDroppable({ id: "email-canvas" });
  const width = mobile ? 360 : numberInRange(design.width, 600, 360, 800);
  const canvasStyle = {
    width: `min(100%, ${width}px)`,
    backgroundColor: safeColor(design.content_background, "#ffffff"),
    color: safeColor(design.text_color, "#334155"),
    fontFamily: design.font_family || DEFAULT_EMAIL_DESIGN.font_family,
  };

  return (
    <div className="min-w-0 overflow-auto px-5 py-7 sm:px-8">
      <div ref={setNodeRef} style={canvasStyle} className={`keep-light mx-auto min-h-[440px] overflow-hidden rounded-sm border shadow-[0_12px_30px_rgba(15,23,42,0.12)] transition ${isOver ? "ring-2 ring-teal-500 ring-offset-4" : ""}`}>
        {blocks.length ? (
          <SortableContext items={blocks.map((block) => block.id)} strategy={verticalListSortingStrategy}>
            {blocks.map((block) => <EmailBlockPreview key={block.id} block={block} selected={block.id === activeBlockId} onSelect={() => onSelect(block.id)} />)}
          </SortableContext>
        ) : (
          <div className="flex min-h-[440px] flex-col items-center justify-center px-8 text-center text-slate-400">
            <LayoutTemplate className="mb-3 h-9 w-9 text-slate-300" />
            <p className="text-sm font-medium text-slate-500">Inizia a comporre la tua email</p>
            <p className="mt-1 text-xs">Trascina un blocco qui oppure selezionalo dalla colonna a sinistra.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EmailMarketingVisualEditor({
  blocks,
  design,
  templates,
  campaignName,
  onBlocksChange,
  onDesignChange,
  onApplyTemplate,
  onSaveTemplate,
  onDeleteTemplate,
}) {
  const [sidebarTab, setSidebarTab] = useState("content");
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const [mobilePreview, setMobilePreview] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );
  const activeBlock = useMemo(() => blocks.find((block) => block.id === activeBlockId) || null, [activeBlockId, blocks]);
  const activeTemplate = templates.find((template) => template.id === selectedTemplateId) || null;

  const addBlock = (type, atIndex = blocks.length) => {
    const item = defaultBlock(type);
    const next = [...blocks];
    next.splice(Math.max(0, atIndex), 0, item);
    onBlocksChange(next);
    setActiveBlockId(item.id);
    setSidebarTab("style");
  };

  const updateBlock = (id, field, value) => {
    onBlocksChange(blocks.map((block) => block.id === id ? { ...block, [field]: value } : block));
  };

  const removeBlock = (id) => {
    onBlocksChange(blocks.filter((block) => block.id !== id));
    if (id === activeBlockId) setActiveBlockId(null);
  };

  const selectBlock = (id) => {
    setActiveBlockId(id);
    setSidebarTab("style");
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    const draggedType = active.data.current?.blockType;
    const overIndex = blocks.findIndex((block) => block.id === over.id);
    if (active.data.current?.source === "palette" && draggedType) {
      addBlock(draggedType, overIndex < 0 ? blocks.length : overIndex);
      return;
    }
    const oldIndex = blocks.findIndex((block) => block.id === active.id);
    if (oldIndex < 0) return;
    const targetIndex = overIndex < 0 ? blocks.length - 1 : overIndex;
    if (oldIndex !== targetIndex) onBlocksChange(arrayMove(blocks, oldIndex, targetIndex));
  };

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((item) => item.id === templateId);
    if (!template) return;
    onApplyTemplate(template);
    setActiveBlockId(null);
  };

  const createTemplate = () => {
    const name = templateName.trim();
    if (!name) return;
    onSaveTemplate({ nome: name, blocks, design });
    setTemplateName("");
    setCreatingTemplate(false);
  };

  return (
    <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-teal-100 text-teal-700"><Mail className="h-4 w-4" /></div>
          <div><p className="text-sm font-semibold text-slate-800">Editor grafico email</p><p className="text-xs text-slate-500">Trascina i blocchi e personalizza il modello.</p></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Carica un modello salvato" className="h-9 max-w-48 rounded-md border bg-white px-2 text-sm text-slate-700" value={selectedTemplateId} onChange={(event) => applyTemplate(event.target.value)}>
            <option value="">Carica un modello</option>
            {templates.map((template) => <option key={template.id} value={template.id}>{template.nome}</option>)}
          </select>
          {activeTemplate && <Button type="button" variant="outline" size="sm" onClick={() => onSaveTemplate({ id: activeTemplate.id, nome: activeTemplate.nome, blocks, design })}><Save className="mr-1 h-4 w-4" />Aggiorna modello</Button>}
          <Button type="button" variant="outline" size="sm" onClick={() => setCreatingTemplate((value) => !value)}><FilePlus2 className="mr-1 h-4 w-4" />Salva modello</Button>
          {activeTemplate && <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => { onDeleteTemplate(activeTemplate); setSelectedTemplateId(""); }} aria-label="Elimina modello"><Trash2 className="h-4 w-4" /></Button>}
          <div className="ml-1 inline-flex rounded-md border p-0.5">
            <button type="button" className={`rounded p-1.5 ${!mobilePreview ? "bg-teal-100 text-teal-800" : "text-slate-500"}`} onClick={() => setMobilePreview(false)} aria-label="Anteprima desktop"><Monitor className="h-4 w-4" /></button>
            <button type="button" className={`rounded p-1.5 ${mobilePreview ? "bg-teal-100 text-teal-800" : "text-slate-500"}`} onClick={() => setMobilePreview(true)} aria-label="Anteprima mobile"><Smartphone className="h-4 w-4" /></button>
          </div>
        </div>
        {creatingTemplate && (
          <div className="flex w-full flex-wrap items-center justify-end gap-2 rounded-lg bg-teal-50 p-2">
            <Input className="h-8 max-w-xs bg-white" placeholder="Nome del modello" value={templateName} onChange={(event) => setTemplateName(event.target.value)} onKeyDown={(event) => event.key === "Enter" && createTemplate()} />
            <Button type="button" size="sm" onClick={createTemplate} disabled={!templateName.trim()}><Save className="mr-1 h-3.5 w-3.5" />Conferma</Button>
          </div>
        )}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={({ active }) => setActiveDrag(active.data.current?.blockType || null)} onDragCancel={() => setActiveDrag(null)} onDragEnd={handleDragEnd}>
        <div className="grid min-h-[600px] lg:grid-cols-[248px_minmax(0,1fr)]">
          <aside className="border-b bg-slate-50 lg:border-b-0 lg:border-r">
            <div className="flex border-b bg-white">
              <button type="button" className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-3 text-sm font-medium ${sidebarTab === "content" ? "border-b-2 border-teal-600 text-teal-700" : "text-slate-500"}`} onClick={() => setSidebarTab("content")}><LayoutTemplate className="h-4 w-4" />Contenuto</button>
              <button type="button" className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-3 text-sm font-medium ${sidebarTab === "style" ? "border-b-2 border-teal-600 text-teal-700" : "text-slate-500"}`} onClick={() => setSidebarTab("style")}><Palette className="h-4 w-4" />Stile</button>
            </div>
            <div className="max-h-[620px] overflow-y-auto p-3">
              {sidebarTab === "content" ? (
                <div>
                  <p className="mb-3 text-xs leading-relaxed text-slate-500">Trascina un blocco nella tela oppure fai clic per aggiungerlo in fondo.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {BLOCK_LIBRARY.map((definition) => <PaletteBlock key={definition.type} definition={definition} onAdd={addBlock} />)}
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <section className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Layout email</p>
                    <RangeField label="Larghezza del corpo" value={design.width} onChange={(value) => onDesignChange({ ...design, width: value })} min={360} max={800} />
                    <EditorField label="Carattere">
                      <select className="h-9 w-full rounded-md border bg-white px-2 text-sm" value={design.font_family || DEFAULT_EMAIL_DESIGN.font_family} onChange={(event) => onDesignChange({ ...design, font_family: event.target.value })}>
                        {FONT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </EditorField>
                    <ColorField label="Sfondo esterno" value={design.body_background} onChange={(value) => onDesignChange({ ...design, body_background: value })} />
                    <ColorField label="Sfondo del corpo" value={design.content_background} onChange={(value) => onDesignChange({ ...design, content_background: value })} />
                    <ColorField label="Colore testo predefinito" value={design.text_color} onChange={(value) => onDesignChange({ ...design, text_color: value })} />
                  </section>
                  <section className="border-t pt-4"><BlockSettings block={activeBlock} onUpdate={updateBlock} onDelete={removeBlock} /></section>
                </div>
              )}
            </div>
          </aside>
          <main className="keep-light min-w-0" style={{ backgroundColor: safeColor(design.body_background, "#f5f7f8"), backgroundImage: "radial-gradient(#cbd5e1 0.8px, transparent 0.8px)", backgroundSize: "14px 14px" }}>
            <EmailCanvas blocks={blocks} design={design} activeBlockId={activeBlockId} onSelect={selectBlock} mobile={mobilePreview} />
          </main>
        </div>
        <DragOverlay dropAnimation={null}>
          {activeDrag && <div className="rounded-lg border border-teal-300 bg-white px-3 py-2 text-sm font-medium text-teal-800 shadow-lg">Aggiungi: {BLOCK_LIBRARY.find((item) => item.type === activeDrag)?.label}</div>}
        </DragOverlay>
      </DndContext>
    </section>
  );
}
